import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  Inbox,
  Settings,
  Clock,
  Phone,
  FileText,
  Star,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Header } from '../components/Header';
import { StatsCard } from '../components/StatsCard';
import { QuickActionCard } from '../components/QuickActionCard';
import { AccountSidebar } from '../components/AccountSidebar';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
import { DeletionSuccessModal } from '../components/DeletionSuccessModal';
import { CreateProviderProfileModal } from '../components/CreateProviderProfileModal';
import { ReviewCard } from '../components/ReviewCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../constants';
import { createProviderProfile, getBookingHistory, getProviderReviews, getUpcomingBookings } from '../services/api';
import { ProviderProfileFormData, Booking, Review } from '../types';

export const ProviderHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, deleteAccount, logout, updateProviderProfileStatus } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeletionSuccessModal, setShowDeletionSuccessModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [reviewCount, setReviewCount] = useState<number>(0);
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  useEffect(() => {
    if (user && user.role === 'provider') {
      const profileStatus = user.hasProviderProfile ?? false;
      setHasProfile(profileStatus);
      setShowProfileModal(!profileStatus);

      if (profileStatus) {
        fetchBookings();
        fetchReviews();
        fetchUpcomingBookings();
      }
    }
  }, [user]);

  const fetchBookings = async () => {
    try {
      const response = await getBookingHistory({ page: 1, limit: 10 });
      if (response.success) {
        const bookingsData = response.data || [];
        setBookings(bookingsData);
        const pending = bookingsData.filter((b: Booking) =>
          b.status === 'Pending' || b.status?.toLowerCase() === 'pending'
        ).length;
        setPendingCount(pending);
      }
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    }
  };

  const fetchUpcomingBookings = async () => {
    setUpcomingLoading(true);
    try {
      const response = await getUpcomingBookings();
      if (response.success) {
        setUpcomingBookings(response.bookings);
      }
    } catch (err) {
      console.error('Failed to fetch upcoming bookings:', err);
    } finally {
      setUpcomingLoading(false);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await getProviderReviews();
      if (response.success) {
        setReviews(response.reviews);
        setAverageRating(response.summary.averageRating || 0);
        setReviewCount(response.summary.totalReviews || response.reviews.length);
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleDeleteAccount = async (reason: string) => {
    const result = await deleteAccount(reason);

    if (result.success) {
      setShowDeleteModal(false);
      setTimeout(() => setShowDeletionSuccessModal(true), 0);
    }

    return result;
  };

  const handleProfileSubmit = async (formData: ProviderProfileFormData) => {
    const response = await createProviderProfile(formData);

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error || 'Failed to create profile'
    };
  };

  const handleProfileSuccess = () => {
    setShowProfileModal(false);
    setHasProfile(true);
    updateProviderProfileStatus(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 relative">
      {!hasProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40" />
      )}

      <Header
        theme="green"
        showNotifications={hasProfile}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!hasProfile && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-600 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Profile Completion Required
                </p>
                <p className="mt-1 text-sm text-green-700">
                  Please complete your provider profile to access all features and start receiving service requests.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name || 'Provider'}
          </h1>
          <p className="text-gray-600">
            Manage your care services and client relationships
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 ${!hasProfile ? 'opacity-50 pointer-events-none' : ''}`}>
          <StatsCard
            title="Pending Service Requests"
            value={pendingCount}
            icon={Inbox}
            theme="green"
            trend={pendingCount > 0 ? { value: `${pendingCount} awaiting response`, isPositive: false } : undefined}
          />
          <StatsCard
            title="Active Bookings"
            value={bookings.filter(b => b.status === 'Accepted').length}
            icon={Users}
            theme="green"
          />
          <StatsCard
            title="Total Bookings"
            value={bookings.length}
            icon={Calendar}
            theme="green"
            trend={bookings.filter(b => b.status === 'Completed').length > 0 ? { value: `${bookings.filter(b => b.status === 'Completed').length} completed`, isPositive: true } : undefined}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className={!hasProfile ? 'opacity-50 pointer-events-none' : ''}>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <QuickActionCard
                  title="View Service Requests"
                  description="Review and respond to new care requests"
                  icon={Inbox}
                  theme="green"
                  onClick={() => hasProfile && navigate(ROUTES.PROVIDER_BOOKINGS)}
                />
                <QuickActionCard
                  title="Browse Care Requests"
                  description="Find and connect with service users"
                  icon={FileText}
                  theme="green"
                  onClick={() => hasProfile && navigate(ROUTES.ALL_POSTS)}
                />
                <QuickActionCard
                  title="Messages"
                  description="View and respond to user messages"
                  icon={MessageSquare}
                  theme="green"
                  onClick={() => hasProfile && navigate(ROUTES.MESSAGES)}
                />
                <QuickActionCard
                  title="Business Settings"
                  description="Update your profile and service offerings"
                  icon={Settings}
                  theme="green"
                  onClick={() => hasProfile && navigate(ROUTES.SETTINGS)}
                />
              </div>
            </div>

            <div className={!hasProfile ? 'opacity-50 pointer-events-none' : ''}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Upcoming Bookings</h2>
                {upcomingBookings.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.PROVIDER_BOOKINGS)}
                  >
                    Manage All
                  </Button>
                )}
              </div>
              {upcomingLoading ? (
                <div className="flex justify-center py-12 bg-white rounded-xl border border-gray-200">
                  <LoadingSpinner size="md" />
                </div>
              ) : upcomingBookings.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
                  <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium mb-1">No upcoming bookings</p>
                  <p className="text-sm text-gray-500">
                    New booking requests will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingBookings.map((booking) => (
                    <div
                      key={booking._id}
                      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {booking.patientSnapshot?.recipientName
                                  ? `Care for ${booking.patientSnapshot.recipientName}`
                                  : `Booking #${booking.bookingId}`}
                              </h3>
                              <p className="text-xs text-gray-500">Booking #{booking.bookingId}</p>
                            </div>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              booking.status === 'Accepted' ? 'bg-green-100 text-green-700' :
                              booking.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {booking.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 font-medium mb-2">{booking.serviceType}</p>
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(booking.appointmentDate)}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Clock className="h-3.5 w-3.5" />
                              {new Date(booking.appointmentDate).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                            {booking.patientSnapshot?.mobilityStatus && (
                              <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                {booking.patientSnapshot.mobilityStatus}
                              </span>
                            )}
                          </div>
                          {booking.notes && (
                            <p className="text-xs text-gray-500 mt-1.5 italic">"{booking.notes}"</p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="self-start"
                          onClick={() => navigate(ROUTES.PROVIDER_BOOKINGS)}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={`bg-white rounded-xl border border-gray-200 p-6 ${!hasProfile ? 'opacity-50 pointer-events-none' : ''}`}>
              <h3 className="font-semibold text-gray-900 mb-4">Business Metrics</h3>
              {reviewsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {averageRating > 0 ? averageRating.toFixed(1) : '—'}
                    </p>
                    <p className="text-sm text-gray-600">Avg Rating</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">98%</p>
                    <p className="text-sm text-gray-600">Response Rate</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {bookings.filter(b => b.status === 'Completed').length}
                    </p>
                    <p className="text-sm text-gray-600">Completed Jobs</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{reviewCount}</p>
                    <p className="text-sm text-gray-600">Reviews</p>
                  </div>
                </div>
              )}
            </div>

            <div className={`bg-white rounded-xl border border-gray-200 p-6 ${!hasProfile ? 'opacity-50 pointer-events-none' : ''}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  Your Reviews
                </h3>
                {reviewCount > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                    </p>
                  </div>
                )}
              </div>

              {reviewsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : reviews.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium mb-1">No reviews yet</p>
                  <p className="text-sm text-gray-500">
                    Your reviews from completed bookings will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.slice(0, 5).map((review) => (
                    <ReviewCard key={review._id} review={review} />
                  ))}
                  {reviews.length > 5 && (
                    <div className="text-center pt-2">
                      <p className="text-sm text-gray-500">
                        Showing 5 of {reviewCount} reviews
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <AccountSidebar
              name={user?.name || 'Provider'}
              email={user?.email || 'provider@example.com'}
              accountType="Care Provider"
              memberSince={new Date(user?.createdAt || Date.now()).toLocaleDateString('en-GB', {
                month: 'short',
                year: 'numeric',
              })}
              theme="green"
              verified={true}
              onDeleteAccount={() => setShowDeleteModal(true)}
              userRole="provider"
            />

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Provider Support</h3>
              <p className="text-sm text-gray-600 mb-4">
                Need help managing your account or have questions about our platform?
              </p>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <Phone className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Help Center
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirmDelete={handleDeleteAccount}
        userRole="provider"
      />

      <DeletionSuccessModal
        isOpen={showDeletionSuccessModal}
        onClose={async () => {
          setShowDeletionSuccessModal(false);
          await logout();
        }}
      />

      <CreateProviderProfileModal
        isOpen={showProfileModal}
        providerId={user?.userId || (user?.providerId ? String(user.providerId) : '')}
        onSuccess={handleProfileSuccess}
        onSubmit={handleProfileSubmit}
      />

    </div>
  );
};
